import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '../App';
import { Calendar as CalendarIcon, Clock, Video, ArrowLeft } from 'lucide-react';

export default function Calendar() {
  const navigate = useNavigate();
  // Load Go High Level form embed script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://links.roxvegas.cloud/js/form_embed.js';
    script.type = 'text/javascript';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 py-16">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8 group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span>Back</span>
        </button>
        
        <div className="mb-12 text-center">
          <div className="inline-flex items-center gap-3 bg-primary/10 border border-primary/30 rounded-full px-6 py-3 mb-6">
            <CalendarIcon className="w-6 h-6 text-primary" />
            <span className="text-primary font-semibold">Book Your Session</span>
          </div>
          <h1 className="text-5xl font-heading font-bold mb-6">Event Calendar</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            View upcoming live sessions, workshops, and coaching calls. Book your spot today!
          </p>
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-card border border-border rounded-xl p-6 text-center">
            <Video className="w-8 h-8 text-primary mx-auto mb-3" />
            <h3 className="font-semibold mb-2">Live Sessions</h3>
            <p className="text-sm text-muted-foreground">Join interactive poker strategy discussions</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-6 text-center">
            <Clock className="w-8 h-8 text-primary mx-auto mb-3" />
            <h3 className="font-semibold mb-2">One-on-One Coaching</h3>
            <p className="text-sm text-muted-foreground">Book private sessions with our experts</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-6 text-center">
            <CalendarIcon className="w-8 h-8 text-primary mx-auto mb-3" />
            <h3 className="font-semibold mb-2">Workshops</h3>
            <p className="text-sm text-muted-foreground">Attend specialized skill-building workshops</p>
          </div>
        </div>

        {/* Go High Level Calendar Widget Container */}
        <div className="bg-card border border-border rounded-2xl p-8 shadow-xl">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-heading font-bold mb-2">Schedule Your Session</h2>
            <p className="text-muted-foreground">Select a date and time that works for you</p>
          </div>

          {/* Calendar Widget Embed Area */}
          <div className="rounded-xl overflow-hidden" style={{minHeight: '800px', width: '100%'}}>
            <iframe 
              src="https://links.roxvegas.cloud/widget/booking/6KOPTZJPVF1SM2g2vvNV" 
              style={{
                width: '100%', 
                height: '800px',
                border: 'none',
                display: 'block'
              }} 
              scrolling="no" 
              id="6KOPTZJPVF1SM2g2vvNV_1774142880932"
              title="Book Your Session"
              allow="payment"
            />
          </div>

          {/* Instructions */}
          <div className="mt-8 border-t border-border pt-8">
            <h3 className="font-semibold mb-4">How to Book:</h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="bg-background/50 rounded-lg p-4">
                <span className="inline-block w-6 h-6 bg-primary text-black rounded-full text-center font-bold mb-2">1</span>
                <p className="text-muted-foreground">Select a date from the calendar above</p>
              </div>
              <div className="bg-background/50 rounded-lg p-4">
                <span className="inline-block w-6 h-6 bg-primary text-black rounded-full text-center font-bold mb-2">2</span>
                <p className="text-muted-foreground">Choose an available time slot</p>
              </div>
              <div className="bg-background/50 rounded-lg p-4">
                <span className="inline-block w-6 h-6 bg-primary text-black rounded-full text-center font-bold mb-2">3</span>
                <p className="text-muted-foreground">Complete your booking details</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Support */}
        <div className="mt-12 text-center">
          <p className="text-muted-foreground mb-4">
            Need help booking or have questions?
          </p>
          <a 
            href="/contact" 
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-semibold transition-colors"
          >
            Contact Us →
          </a>
        </div>
      </div>
    </div>
  );
}